import type { MapBlocker, MapDocument, MapFloorLevel, MapFloorPanel, MapWallSegment } from '../content/schema/map';
import { findStairRun, type FloorYMap, type StairRun, type StairTrigger } from './floorStair';
import type { PlayerMapData } from './FirstPersonController';

/** Default vertical spacing between floor planes when a project gives none. */
export const DEFAULT_STORY_HEIGHT = 3.2;

/**
 * Default maximum height (metres) a player can step up to reach a floor plane.
 * Low ledges are traversable; anything taller needs a stair trigger. Applied by
 * `makeWalkable`/`deriveMapData` when no explicit `stepHeight` is given, so the
 * threshold is active by default rather than disabled.
 */
export const DEFAULT_STEP_HEIGHT = 0.6;

/**
 * Default height (metres) above which a wall is walked *under* rather than
 * into.
 *
 * Walls block by their axis-aligned x/z footprint, with no regard to where they
 * sit vertically - which is right for a wall and wrong for a lintel, a soffit
 * or a beam. Without this, filling the strip above a doorway (so it stops being
 * a floor-to-ceiling hole) would seal the doorway to the player.
 *
 * Set a little above head height, so anything a player can duck beneath is
 * ignored and anything they would walk into still blocks.
 */
export const DEFAULT_WALK_UNDER_HEIGHT = 1.9;

export interface MapRuntimeOptions {
  /** Explicit world-Y per floor level. Missing levels fall back to level*story. */
  floorHeights?: Partial<Record<MapFloorLevel, number>>;
  /** Spacing used for the level*story fallback. */
  storyHeight?: number;
  /**
   * Maximum vertical distance (metres) the player can step up to reach a floor
   * plane. When a `playerY` is supplied to `makeWalkable`, only floors whose
   * world-Y is within this tolerance of `playerY` are tested.
   *
   * Defaults to {@link DEFAULT_STEP_HEIGHT}. The gate only takes effect when a
   * `playerY` is supplied to the `makeWalkable` predicate, so callers that omit
   * it (geometry-only walkability tests) are unaffected.
   */
  stepHeight?: number;
  /**
   * Height (metres) at or above which a wall's underside means the player walks
   * under it instead of into it - lintels, soffits, beams. Defaults to
   * {@link DEFAULT_WALK_UNDER_HEIGHT}.
   */
  walkUnderHeight?: number;
}

/** World-Y of a floor's walkable plane. */
export function floorWorldY(
  level: MapFloorLevel,
  options?: MapRuntimeOptions,
): number {
  const explicit = options?.floorHeights?.[level];
  if (typeof explicit === 'number') return explicit;
  return level * (options?.storyHeight ?? DEFAULT_STORY_HEIGHT);
}

/** Build the floor-index -> world-Y map for every floor in the document. */
export function buildFloorYMap(
  document: MapDocument,
  options?: MapRuntimeOptions,
): FloorYMap {
  const map: FloorYMap = {};
  for (const floor of document.floors) {
    map[floor.level] = floorWorldY(floor.level, options);
  }
  return map;
}

/**
 * Vertical margin (metres either side of the viewer's own floor) within
 * which another floor still counts as visible - wide enough to include the
 * floor immediately above or below (so a stairwell or landing does not
 * visibly pop as the viewer crosses between floors) but not a floor beyond
 * that. Expressed as a multiple of a storey's height rather than a fixed
 * distance so it scales with a project's own `storyHeight`.
 */
const FLOOR_VISIBILITY_STORY_MULTIPLE = 1.5;

/**
 * Whether a floor at `level` should be rendered for a viewer currently on
 * `currentLevel`. Distance is measured in world-Y (via {@link floorWorldY},
 * which already honours a project's explicit per-level `floorHeights`
 * override) rather than raw level number, so uneven storey heights still
 * cull correctly - two floors of different heights three levels apart are
 * not assumed "close" just because their level numbers are.
 */
export function isFloorLevelVisible(
  level: MapFloorLevel,
  currentLevel: MapFloorLevel,
  options?: MapRuntimeOptions,
): boolean {
  const threshold = (options?.storyHeight ?? DEFAULT_STORY_HEIGHT) * FLOOR_VISIBILITY_STORY_MULTIPLE;
  return Math.abs(floorWorldY(level, options) - floorWorldY(currentLevel, options)) <= threshold;
}

/**
 * Tolerance for panel containment, in metres.
 *
 * Panels that share an edge are authored to the same coordinate, but a centre
 * and a half-size reconstruct that edge through floating point: two panels
 * meeting at x = 1.3 can end up spanning to 1.2999999999999998 and from
 * 1.3000000000000003, leaving a crack narrower than an atom that a point
 * landing exactly on the boundary falls into. A doorway threshold sits exactly
 * on such a boundary, so the crack is reachable in practice. One micrometre of
 * slack closes it without letting the player stand anywhere they should not.
 */
const PANEL_EDGE_EPSILON = 1e-6;

function pointInRect(
  x: number,
  z: number,
  center: [number, number],
  size: [number, number],
  margin = 0,
): boolean {
  const halfW = size[0] / 2 + margin;
  const halfD = size[1] / 2 + margin;
  return (
    x >= center[0] - halfW &&
    x <= center[0] + halfW &&
    z >= center[1] - halfD &&
    z <= center[1] + halfD
  );
}

/**
 * World size (metres) of one grid cell in the broad-phase index below.
 *
 * Fixed rather than derived from a document: deriving a per-floor size would
 * still need to run once per floor for no real benefit, and this is already
 * the right order of magnitude for the geometry it indexes - wall runs and
 * room panels of a few metres, blockers well under two. Items are inserted
 * into every cell their own footprint touches, so a run longer than one cell
 * is still indexed correctly; it just occupies more than one bucket.
 */
const GRID_CELL_SIZE = 3;

/**
 * A uniform spatial hash over one floor's walls, blockers or panels, so
 * `makeWalkable`'s predicate - called several times a frame - only tests the
 * handful of items actually near a query point instead of scanning every
 * item on the floor on every call. This was flagged as fine at a single
 * house's scale but a real scaling risk once a map covers a street's worth
 * of houses (see the downstream game's own performance notes).
 *
 * A broad phase only, never the source of truth: `near()` returns every item
 * whose own footprint could possibly satisfy a margin-expanded point test at
 * `(x, z)` - a safe superset, not the exact answer - and the caller still
 * runs the real `pointInRect` test on what it returns. That is what makes the
 * margin (the player radius, or `PANEL_EDGE_EPSILON`) safe to leave out of
 * the index itself even though it varies per call: expanding the *query*
 * region by the same margin instead is geometrically equivalent, and does
 * not require rebuilding the grid per radius.
 */
class SpatialGrid<T> {
  private readonly cells = new Map<string, T[]>();

  constructor(
    items: readonly T[],
    bounds: (item: T) => { center: readonly [number, number]; size: readonly [number, number] },
    private readonly cellSize: number = GRID_CELL_SIZE,
  ) {
    for (const item of items) {
      const { center, size } = bounds(item);
      const halfW = size[0] / 2;
      const halfD = size[1] / 2;
      const minCx = Math.floor((center[0] - halfW) / cellSize);
      const maxCx = Math.floor((center[0] + halfW) / cellSize);
      const minCz = Math.floor((center[1] - halfD) / cellSize);
      const maxCz = Math.floor((center[1] + halfD) / cellSize);
      for (let cx = minCx; cx <= maxCx; cx += 1) {
        for (let cz = minCz; cz <= maxCz; cz += 1) {
          const key = `${cx}:${cz}`;
          const bucket = this.cells.get(key);
          if (bucket) bucket.push(item);
          else this.cells.set(key, [item]);
        }
      }
    }
  }

  /**
   * Every item that could possibly overlap `(x, z)` expanded by `margin` -
   * see the class doc comment for why this is a superset, not an exact
   * answer, and why that is still correct. De-duplicated, since an item
   * spanning more than one cell would otherwise come back more than once.
   */
  near(x: number, z: number, margin: number): T[] {
    const minCx = Math.floor((x - margin) / this.cellSize);
    const maxCx = Math.floor((x + margin) / this.cellSize);
    const minCz = Math.floor((z - margin) / this.cellSize);
    const maxCz = Math.floor((z + margin) / this.cellSize);
    // The overwhelmingly common case - a query margin well under one cell -
    // touches exactly one cell, so skip the multi-cell dedupe machinery below.
    if (minCx === maxCx && minCz === maxCz) {
      return this.cells.get(`${minCx}:${minCz}`) ?? [];
    }
    const seen = new Set<T>();
    const out: T[] = [];
    for (let cx = minCx; cx <= maxCx; cx += 1) {
      for (let cz = minCz; cz <= maxCz; cz += 1) {
        const bucket = this.cells.get(`${cx}:${cz}`);
        if (!bucket) continue;
        for (const item of bucket) {
          if (!seen.has(item)) {
            seen.add(item);
            out.push(item);
          }
        }
      }
    }
    return out;
  }
}

/** Broad-phase index over one floor's collidable geometry. */
interface FloorIndex {
  doc: MapDocument['floors'][number];
  panels: SpatialGrid<MapFloorPanel>;
  blockers: SpatialGrid<MapBlocker>;
  /** Only walls whose underside does not clear `walkUnderHeight` - the ones
   *  a lintel or soffit represents are excluded once here rather than tested
   *  and discarded on every query. */
  walls: SpatialGrid<MapWallSegment>;
}

function buildFloorIndex(
  floorDoc: MapDocument['floors'][number],
  walkUnderHeight: number,
): FloorIndex {
  const collidingWalls = floorDoc.walls.filter(
    (wall) => wall.position[1] - wall.size[1] / 2 < walkUnderHeight,
  );
  return {
    doc: floorDoc,
    panels: new SpatialGrid(floorDoc.panels, (p) => ({ center: p.center, size: p.size })),
    blockers: new SpatialGrid(floorDoc.blockers, (b) => ({ center: b.center, size: b.size })),
    walls: new SpatialGrid(collidingWalls, (w) => ({
      center: [w.position[0], w.position[2]],
      size: [w.size[0], w.size[2]],
    })),
  };
}

/** Is the point inside any indexed wall on this floor, grown by the player radius? */
function isWalled(index: FloorIndex, x: number, z: number, radius: number): boolean {
  return index.walls
    .near(x, z, radius)
    .some((wall) => pointInRect(x, z, [wall.position[0], wall.position[2]], [wall.size[0], wall.size[2]], radius));
}

/**
 * Derive a walkability predicate from a floor's panels, walls and blockers: a
 * point is walkable when it lies within some floor panel and outside every
 * blocker and wall footprint (both grown by the player radius). Wall
 * footprints are their axis-aligned x/z extents (the Map Builder draws
 * axis-locked walls; a rotated wall blocks by its bounding box). Returns
 * `false` for unknown floors.
 *
 * Walls whose underside sits at or above `walkUnderHeight` are skipped, so a
 * lintel over a doorway does not seal the doorway.
 *
 * @param document - The map document whose geometry is used.
 * @param options  - Optional runtime options; when `stepHeight` is set and
 *   `playerY` is passed to the returned predicate, only floors within
 *   `stepHeight` metres of `playerY` are tested.
 */
export function makeWalkable(document: MapDocument, options?: MapRuntimeOptions) {
  const floorY = buildFloorYMap(document, options);
  const stepHeight = options?.stepHeight ?? DEFAULT_STEP_HEIGHT;
  const walkUnderHeight = options?.walkUnderHeight ?? DEFAULT_WALK_UNDER_HEIGHT;
  const runs = buildStairRuns(document);

  // Built once per document rather than scanned per call: a floor's walls,
  // blockers and panels do not change while this predicate is in use, so the
  // broad-phase index only needs to exist once, not be rebuilt (or its
  // source arrays re-walked in full) on every one of the several calls a
  // single frame makes. Keyed by level rather than re-running
  // `document.floors.find` per call, for the same reason.
  const floorIndex = new Map<number, FloorIndex>(
    document.floors.map((floorDoc) => [floorDoc.level, buildFloorIndex(floorDoc, walkUnderHeight)]),
  );

  return (
    x: number,
    z: number,
    radius: number,
    floor: number,
    /** Current world-Y of the player. When provided, the floor must be within
     *  `stepHeight` metres of this value; otherwise the floor is skipped. */
    playerY?: number,
  ): boolean => {
    const index = floorIndex.get(floor);
    if (!index) return false;

    // A flight is the surface where it lies, so no floor panel is expected
    // under it and the step-height gate does not apply. Its own footprint
    // (tested above by findStairRun, with no inset) is already the exact
    // walkable envelope - the void it passes through is walled identically to
    // a room, so those walls sit centred ON the run's own edges. Growing that
    // collision by the player radius on top of the run's own box test would
    // double-bound the same edges and eat deep into the flight's width from
    // both sides at once: on a 1.2 m bay, half a wall's thickness (0.12 m)
    // plus the player radius (0.28 m) blocks 0.40 m from each side, leaving a
    // sliver down the centre as the only walkable strip for the flight's
    // entire length - and pins anyone who drifts off it against a wall they
    // cannot step back from without also moving off the stairs.
    const run = findStairRun(x, z, runs);
    if (run && (run.fromFloor === floor || run.toFloor === floor)) {
      return !index.blockers
        .near(x, z, radius)
        .some((blocker) => pointInRect(x, z, blocker.center, blocker.size, radius));
    }

    // Step-height gate: if the caller supplies a playerY and the floor plane is
    // further away than stepHeight, the player cannot step up to it.
    if (playerY !== undefined && stepHeight < Infinity) {
      const fy = floorY[floor];
      if (fy !== undefined && Math.abs(fy - playerY) > stepHeight) return false;
    }

    const onPanel = index.panels
      .near(x, z, PANEL_EDGE_EPSILON)
      .some((panel) => pointInRect(x, z, panel.center, panel.size, PANEL_EDGE_EPSILON));
    if (!onPanel) return false;
    const blocked = index.blockers
      .near(x, z, radius)
      .some((blocker) => pointInRect(x, z, blocker.center, blocker.size, radius));
    if (blocked) return false;
    return !isWalled(index, x, z, radius);
  };
}

/** Map document stair runs onto the runtime shape. */
export function buildStairRuns(document: MapDocument): StairRun[] {
  return (document.stairRuns ?? []).map((run) => ({
    id: run.id,
    fromFloor: run.fromFloor,
    toFloor: run.toFloor,
    axis: run.axis,
    center: run.center,
    size: run.size,
    footAt: run.footAt,
    // Previously dropped here even though the document carried it - the
    // schema's own comment called this "for renderers" only, so
    // stairRunHeight had no way to know a flight was stepped rather than a
    // smooth ramp. See stairRunHeight's own doc comment (floorStair.ts).
    steps: run.steps,
  }));
}

/** Map document stair triggers onto the runtime controller's trigger shape. */
export function buildStairTriggers(
  document: MapDocument,
  floorY: FloorYMap,
): StairTrigger[] {
  return document.stairTriggers.map((trigger) => ({
    fromFloor: trigger.fromFloor,
    toFloor: trigger.toFloor,
    center: trigger.center,
    size: trigger.size,
    spawnXZ: trigger.spawnXZ,
    toY: floorY[trigger.toFloor] ?? trigger.toY,
  }));
}

/** Resolve the player's spawn from the document settings, with a safe fallback. */
export function resolveSpawn(
  document: MapDocument,
  floorY: FloorYMap,
): PlayerMapData['spawn'] {
  const configured = document.settings?.defaultSpawn;
  if (configured) {
    const floor = configured.floor ?? 0;
    return {
      position: [configured.x, configured.y ?? floorY[floor] ?? 0, configured.z],
      yaw: configured.yaw ?? 0,
      floor,
    };
  }
  const firstFloor = document.floors[0];
  const firstPanel = firstFloor?.panels[0];
  const floor = firstFloor?.level ?? 0;
  const y = floorY[floor] ?? 0;
  return {
    position: firstPanel ? [firstPanel.center[0], y, firstPanel.center[1]] : [0, y, 0],
    yaw: 0,
    floor,
  };
}

/**
 * Build the complete `PlayerMapData` the FirstPersonController needs from a
 * MapDocument. Pure and game-agnostic: floor planes, stair links, walkability,
 * and spawn all come from the document plus optional floor-height overrides.
 */
export function deriveMapData(
  document: MapDocument,
  options?: MapRuntimeOptions,
): PlayerMapData {
  const floorY = buildFloorYMap(document, options);
  return {
    floorY,
    candidateFloors: document.floors.map((floor) => floor.level),
    stairTriggers: buildStairTriggers(document, floorY),
    stairRuns: buildStairRuns(document),
    isWalkable: makeWalkable(document, options),
    spawn: resolveSpawn(document, floorY),
  };
}

